import { createFileRoute, Link } from "@tanstack/react-router";
import {
  BadgeCheck,
  Calculator,
  FileText,
  Landmark,
  Map,
  Receipt,
  Route as RouteIcon,
  Scale,
  Wallet,
} from "lucide-react";
import { DashboardShell, PageHeading, useAggregates } from "@/components/DashboardShell";

export const Route = createFileRoute("/tools")({
  head: () => ({ meta: [{ title: "Trade Tools — Ready4Trade" }] }),
  component: ToolsPage,
});

const TOOLS = [
  {
    to: "/eu-tariff",
    icon: Landmark,
    title: "EU duty & GSP+ calculator",
    text: "CN8 duty, GSP+ preference margin, entry price, REX readiness and VAT for all 27 EU member states.",
  },
  {
    to: "/gsp-plus",
    icon: BadgeCheck,
    title: "GSP+ hub",
    text: "What GSP+ is worth across your pipeline, withdrawal exposure, utilisation and a statement-on-origin generator.",
  },
  {
    to: "/incoterms",
    icon: Scale,
    title: "Incoterms advisor",
    text: "Compare all 11 Incoterms® 2020 rules and get a deterministic shortlist from your delivery choices.",
  },
  {
    to: "/costs",
    icon: Calculator,
    title: "Landed cost",
    text: "Model customs value, duties, taxes, brokerage, handling, inland delivery and landed unit cost.",
  },
  {
    to: "/pricing",
    icon: Receipt,
    title: "Export price planner",
    text: "Build FCA/CPT/CIP/DAP-style cost and target-margin price scenarios.",
  },
  {
    to: "/invoice",
    icon: FileText,
    title: "Commercial invoice",
    text: "Prepare a printable draft invoice with HS code, Incoterm, quantities and line totals.",
  },
  {
    to: "/logistics",
    icon: Map,
    title: "Logistics intelligence",
    text: "Review routes, distance and logistics context already available in the Navigator.",
  },
  {
    to: "/payments",
    icon: Wallet,
    title: "Payments",
    text: "Explore payment-term patterns and practical payment context in the buyer-lead dataset.",
  },
  {
    to: "/eu",
    icon: RouteIcon,
    title: "EU Export Assistant",
    text: "Combine your HS/product context with official EU market-access, tariff, trade-data and news sources.",
  },
] as const;

function ToolsPage() {
  const agg = useAggregates();
  return (
    <DashboardShell meta={agg.data?.meta}>
      <PageHeading
        title="Trade tools"
        subtitle="Deterministic calculators for the numbers; AI where interpretation helps; official sources where customs or regulatory facts must stay current."
        badge="Export deal toolkit"
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {TOOLS.map((tool) => (
          <Link
            key={tool.to}
            to={tool.to}
            className="surface-panel group p-5 transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md"
          >
            <tool.icon className="h-5 w-5 text-primary" />
            <h2 className="mt-3 font-display text-base group-hover:text-primary">{tool.title}</h2>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{tool.text}</p>
            <div className="mt-4 text-xs font-semibold text-primary">Open tool →</div>
          </Link>
        ))}
      </div>
    </DashboardShell>
  );
}
